

import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number>(0);
  const [selectedProviders, setSelectedProviders] = useState<{[key: string]: {selected: boolean, type: string}}>({});
  const [otherProviderText, setOtherProviderText] = useState<string>('');

  const providers = [
    'AST (Adaptive Skills Trainer)',
    'Respite',
    'Personal Assistant',
    'ILS (Independent Living Support)',
    'Daycare',
    'IHSS provider',
    'Coach (Fitness, Executive Function, etc)',
    'Caregiver',
    'Others'
  ];

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToSignup = () => {
    const signupSection = document.getElementById('signup-section');
    if (signupSection) {
      signupSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleProviderChange = (provider: string, field: 'selected' | 'type', value: boolean | string) => {
    setSelectedProviders(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  const handleWaitlistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Add selected providers to form data
    const selectedProvidersList = Object.entries(selectedProviders)
      .filter(([_, data]) => data.selected)
      .map(([provider, data]) => {
        if (provider === 'Others' && otherProviderText) {
          return `${otherProviderText} (${data.type || 'Not specified'})`;
        }
        return `${provider} (${data.type || 'Not specified'})`;
      })
      .join(', ');
    
    if (selectedProvidersList) {
      formData.append('care_providers', selectedProvidersList);
    }
    
    // Convert FormData to URLSearchParams for application/x-www-form-urlencoded
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d3quul8c1ds027kqn4tg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString()
      });

      if (response.ok) {
        // Generate a random waitlist position between 50-200 for demo
        const position = Math.floor(Math.random() * 150) + 50;
        setWaitlistPosition(position);
        setShowConfirmation(true);
        form.reset();
        setSelectedProviders({});
        setOtherProviderText('');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-24">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/d58764125c7980aecc839df102219a66/870d5e2d6ad7495c633feeec16bc35ed.png"
                alt="Care Entry"
                className="h-20 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Home
              </Link>
              <Link to="/how-it-works" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                How It Works
              </Link>
              <a href="#features" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Features
              </a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Testimonials
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-800 cursor-pointer font-medium">
                Pricing
              </a>
              <a
                href="#signup-section"
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-2 rounded-full hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer font-medium"
              >
                Join the Waitlist
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center bg-gradient-to-br from-purple-300 via-purple-400 to-pink-300"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/ebf3ca1ced03b8c45a268f4ba1fa4381.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/60 to-pink-400/40"></div>
        <div className="relative w-full max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-center justify-between">
            <div className="w-full lg:w-1/2 text-white">
              <h1
                className="text-5xl lg:text-6xl font-light mb-6 leading-tight"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Simplify care management for your family.
              </h1>
              <p className="text-xl lg:text-2xl mb-8 text-purple-100 leading-relaxed font-light">
                Built for special needs parent who manages their child's Care Team. Whether that's an Adaptive Skills Trainer, Respite, Personal Assistant, Coaches and other provider in the SDP (Self-Determination Program) or IHSS (In-Home Support Service) program, you can now organize notes, goals, &amp; schedules in one easy app.
              </p>
              <button 
                onClick={scrollToSignup}
                className="bg-white text-purple-700 px-8 py-4 rounded-full text-lg font-medium hover:bg-purple-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Join the Waitlist
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-share-line text-2xl text-purple-600"></i>
              </div>
              <h3
                className="text-xl font-medium mb-3 text-gray-800"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Share updates instantly
              </h3>
              <p className="text-gray-600 font-light">w/your care team</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-pink-600"></i>
              </div>
              <h3
                className="text-xl font-medium mb-3 text-gray-800"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Track medications, moods
              </h3>
              <p className="text-gray-600 font-light">&amp; progress goals</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-folder-line text-2xl text-purple-600"></i>
              </div>
              <h3
                className="text-xl font-medium mb-3 text-gray-800"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Replace paper binders
              </h3>
              <p className="text-gray-600 font-light">w/organized digital notes</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-4">
                <i className="ri-smartphone-line text-2xl text-pink-600"></i>
              </div>
              <h3
                className="text-xl font-medium mb-3 text-gray-800"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Access care info
              </h3>
              <p className="text-gray-600 font-light">anytime, anywhere</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Why Families Are Switching From Paper to Care Entry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Stop fighting with paper binders and start focusing on what matters most
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Old Way */}
            <div className="bg-red-50 rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mr-4">
                  <i className="ri-close-line text-2xl text-red-600"></i>
                </div>
                <h3
                  className="text-2xl font-medium text-red-600"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Old Way (Paper &amp; Binders)
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Caregivers forget to write notes until days later</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Hard to read handwriting, lost papers, inconsistent info</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Parents spend hours pulling reports together before RC/IHSS meetings</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>No easy way to track progress or recurring issues</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Parents need to text/call multiple caregivers for updates</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Binders pile up and get forgotten in a drawer</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-close-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Social workers doubt accuracy without clear documentation</span>
                </li>
              </ul>
            </div>

            {/* New Way */}
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mr-4">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                <h3
                  className="text-2xl font-medium text-green-600"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  New Way (With Care Entry)
                </h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Notes logged instantly after each session</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Clean, timestamped digital records you can't lose</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Generate a professional report in seconds</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>AI helps flag trends and behaviors for you</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>All updates live in one shared app</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Access anywhere on your phone or desktop</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>Notes and reports look professional and consistent</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Everything You Need in One Place
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light">
              Designed specifically for families managing complex care needs
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=IHSS%20caregiver%20using%20smartphone%20to%20document%20care%20notes%20while%20sitting%20with%20client%20in%20home%20setting%2C%20natural%20authentic%20moment%2C%20professional%20yet%20warm%20interaction%2C%20realistic%20home%20environment%2C%20focused%20on%20care%20documentation&width=600&height=400&seq=care-documentation&orientation=landscape"
                alt="Care Documentation"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
            <div>
              <h3
                className="text-3xl font-light text-gray-800 mb-6"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Smart Care Documentation
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Track medications, mood changes, behavioral goals, and daily activities. Everything your care team needs to know,
                organized and searchable.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Medication tracking &amp; reminders
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Behavioral &amp; mood logging
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Progress toward IEP goals
                </li>
              </ul>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=parent%20care%20manager%20reviewing%20shared%20calendar%20on%20tablet%20with%20appointments%20and%20therapy%20sessions%2C%20organized%20home%20office%20setting%2C%20natural%20lighting%2C%20focused%20on%20scheduling%20and%20coordination%2C%20realistic%20family%20management%20scenario&width=600&height=400&seq=care-scheduling&orientation=landscape"
                alt="Care Scheduling"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
            <div className="lg:order-1">
              <h3
                className="text-3xl font-light text-gray-800 mb-6"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Coordinated Scheduling
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Keep track of therapy appointments, respite care, school meetings, and medical visits. Your entire team stays in sync.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Shared calendar for all providers
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Automatic appointment reminders
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Easy schedule updates
                </li>
              </ul>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=SDP%20parent%20manager%20generating%20care%20reports%20on%20laptop%20while%20child%20plays%20nearby%2C%20comfortable%20home%20setting%2C%20natural%20family%20moment%2C%20focus%20on%20progress%20tracking%20and%20reporting%2C%20warm%20authentic%20atmosphere&width=600&height=400&seq=care-reporting&orientation=landscape"
                alt="Care Reporting"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
            <div>
              <h3
                className="text-3xl font-light text-gray-800 mb-6"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Instant Progress Reports
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Generate reports for Regional Center meetings, school IEPs, or medical appointments. All your data organized and ready to share.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  One-click progress summaries
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Export for Regional Center
                </li>
                <li className="flex items-center text-gray-700">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  Share with any provider
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Forum / Community Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Connect With Families &amp; Caregivers Like You
            </h2>
            <p className="text-xl text-gray-600 font-light">A safe space to share resources, advice, &amp; support.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20parents%20and%20caregivers%20in%20supportive%20discussion%20circle%2C%20warm%20community%20meeting%20environment%2C%20natural%20lighting%2C%20authentic%20expressions%20of%20support%20and%20understanding%2C%20inclusive%20and%20welcoming%20atmosphere&width=600&height=400&seq=caregiver-community&orientation=landscape"
                alt="Caregiver Community"
                className="rounded-2xl shadow-2xl w-full object-cover object-top"
              />
            </div>
            <div>
              <h3
                className="text-3xl font-light text-gray-800 mb-6"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                Your Care Team Community
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Connect with other families and the professionals who understand your journey.
              </p>

              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-800 mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Caregivers who can join:
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Adaptive Skills Trainers
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Respite Providers
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Personal Assistants
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Independent Living Coaches
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Tutors &amp; Mentors
                  </div>
                  <div className="flex items-center">
                    <i className="ri-user-line text-purple-600 mr-2"></i>
                    Homemakers &amp; Companions
                  </div>
                </div>
              </div>

              <p className="text-gray-600">
                Share experiences, get advice, and find support from people who truly understand the challenges and joys of care
                management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Getting Started Is Simple
            </h2>
            <p className="text-xl text-gray-600 font-light">Set up your family's care hub in minutes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mx-auto mb-6 text-2xl font-medium">
                1
              </div>
              <h3 className="text-2xl font-medium mb-4 text-gray-800" style={{ fontFamily: '"Playfair Display", serif' }}>
                Set Up Your Family Hub
              </h3>
              <p className="text-gray-600 text-lg font-light">
                Add your loved&apos;s basic info and care goals. Takes just a few minutes to get started.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mx-auto mb-6 text-2xl font-medium">
                2
              </div>
              <h3 className="text-2xl font-medium mb-4 text-gray-800" style={{ fontFamily: '"Playfair Display", serif' }}>
                Invite Your Care Team
              </h3>
              <p className="text-gray-600 text-lg font-light">
                Add IHSS workers, respite providers, therapists, and family members. Everyone gets the access they need.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full mx-auto mb-6 text-2xl font-medium">
                3
              </div>
              <h3 className="text-2xl font-medium mb-4 text-gray-800" style={{ fontFamily: '"Playfair Display", serif' }}>
                Start Documenting Care
              </h3>
              <p className="text-gray-600 text-lg font-light">
                Log daily activities, track goals, and share updates. Your team stays connected and informed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Real Stories from Real Families
            </h2>
            <p className="text-xl text-gray-600 font-light">See how Care Entry is helping families like yours</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg font-light">
                "No more lost paperwork or missed appointments. My son&apos;s IHSS worker and I can finally stay on the same page."
              </p>
              <div className="flex items-center">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20caring%20mother%20with%20tired%20but%20hopeful%20expression%2C%20natural%20lighting%2C%20diverse%20ethnicity%2C%20authentic%20family%20caregiver%20appearance%2C%20warm%20and%20relatable&width=60&height=60&seq=testimonial-maria-revised&orientation=squarish"
                  alt="Maria S."
                  className="w-12 h-12 rounded-full mr-4 object-cover object-top"
                />
                <div>
                  <p className="font-medium text-gray-800">Maria S.</p>
                  <p className="text-gray-600 text-sm font-light">SDP Parent Care Manager</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg font-light">
                "I can document everything right from my phone. Makes my job so much easier and the family knows exactly what
                happened each day."
              </p>
              <div className="flex items-center">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20compassionate%20IHSS%20caregiver%2C%20middle-aged%2C%20natural%20lighting%2C%20diverse%20ethnicity%2C%20caring%20professional%20appearance%2C%20warm%20and%20trustworthy%20expression&width=60&height=60&seq=testimonial-janet-revised&orientation=squarish"
                  alt="Janet R."
                  className="w-12 h-12 rounded-full mr-4 object-cover object-top"
                />
                <div>
                  <p className="font-medium text-gray-800">Janet R.</p>
                  <p className="text-gray-600 text-sm font-light">IHSS Caregiver</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-6 text-lg font-light">
                "Finally ditched the three-ring binder! Everything&apos;s organized and I can pull up reports for Regional Center
                meetings instantly."
              </p>
              <div className="flex items-center">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20headshot%20of%20organized%20father%20with%20special%20needs%20experience%2C%20natural%20lighting%2C%20diverse%20ethnicity%2C%20calm%20and%20confident%20expression%2C%20authentic%20parent%20caregiver%20appearance&width=60&height=60&seq=testimonial-david-revised&orientation=squarish"
                  alt="David M."
                  className="w-12 h-12 rounded-full mr-4 object-cover object-top"
                />
                <div>
                  <p className="font-medium text-gray-800">David M.</p>
                  <p className="text-gray-600 text-sm font-light">Parent &amp; Primary Caregiver</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2
            className="text-4xl font-light text-gray-800 mb-4"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Be First to Experience Care Entry
          </h2>
          <p className="text-xl text-gray-600 mb-12 font-light">
            Join our waitlist and get early access when we launch
          </p>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-medium mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Coming Soon
              </h3>
              <p className="text-lg opacity-90 font-light">Be among the first families to get organized with Care Entry</p>
            </div>

            <div className="mb-8">
              <h4 className="text-xl font-medium text-gray-800 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                What you'll get when we launch:
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Unlimited care team members</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Digital care documentation</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Shared scheduling &amp; reminders</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Progress tracking &amp; goals</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Instant reports for meetings</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Family support community</span>
                </div>
              </div>
            </div>

            <div 
              className="bg-gray-50 rounded-2xl p-6 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
              onClick={scrollToSignup}
            >
              <p className="text-lg text-gray-700 font-medium" style={{ fontFamily: '"Playfair Display", serif' }}>
                Early access benefits for waitlist members
              </p>
              <p className="text-gray-600 mt-2 font-light">
                Special pricing and exclusive features for our first families
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-light text-gray-800 mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              Questions Families Ask
            </h2>
            <p className="text-xl text-gray-600 font-light">Get answers to common concerns about Care Entry</p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Is Care Entry HIPAA compliant?',
                answer:
                  'Care Entry is family-centered and designed for team coordination, not for storing medical records, so HIPAA compliance does not apply. We focus on helping families organize their care teams and daily care activities.',
              },
              {
                question: 'Can my IHSS worker use this too?',
                answer:
                  'Absolutely! IHSS caregivers, respite providers, and other care team members can easily document their time with your loved one. It\'s designed to be simple for everyone to use.',
              },
              {
                question: 'Will this work for Regional Center reporting?',
                answer:
                  'Yes! Care Entry makes it easy to generate progress reports and documentation that you can share at Regional Center meetings, IEP meetings, or with any provider.',
              },
              {
                question: 'Do I need to be tech-savvy?',
                answer:
                  "Not at all. Care Entry is designed for busy families, not tech experts. If you can text or use email, you can use Care Entry. Plus, we're here to help if you need it.",
              },
              {
                question: "What if my care team doesn't want to use technology?",
                answer:
                  'Many families start by having just the parent care manager use Care Entry, then gradually invite team members who are comfortable with it. You can always print reports to share with those who prefer paper.',
              },
              {
                question: 'Can I try it before paying anything?',
                answer:
                  "Yes! You get 14 full days to try everything with no credit card required. If it doesn't help your family get organized, just walk away — no questions asked.",
              },
            ].map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleFaq(index)}
                >
                  <span
                    className="text-lg font-medium text-gray-800"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {faq.question}
                  </span>
                  <i
                    className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-purple-600 text-xl`}
                  ></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed font-light">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Pitch Section */}
      <section id="signup-section" className="py-20 bg-gradient-to-br from-purple-100 to-pink-100 text-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=peaceful%20parent%20relaxing%20at%20home%20with%20sense%20of%20relief%20and%20peace%20of%20mind%20while%20special%20needs%20child%20is%20safely%20supported%20in%20background%2C%20calm%20reassuring%20atmosphere%2C%20warm%20home%20lighting%2C%20authentic%20family%20moment%20showing%20serenity%20and%20confidence&width=800&height=400&seq=peace-of-mind&orientation=landscape"
              alt="Peace of Mind"
              className="rounded-2xl shadow-2xl w-full object-cover object-top mb-8"
            />
          </div>

          <h2
            className="text-4xl md:text-5xl font-light mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Families managing care already have enough on their plates.
          </h2>
          <p className="text-xl mb-8 opacity-90 leading-relaxed font-light">
            Don't let paperwork be one of them. Join our waitlist &amp; be the first to experience Care Entry — w/everything documented,
            organized, &amp; ready when you need it.
          </p>

          {showConfirmation ? (
            <div className="max-w-md mx-auto mb-8 bg-green-50 border border-green-200 rounded-2xl p-8">
              <div className="flex items-center justify-center mb-4">
                <i className="ri-check-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h3 className="text-2xl font-medium text-green-800 mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                You're on the list!
              </h3>
              <p className="text-green-700 text-lg">
                We'll notify you when CareEntry launches. You're #{waitlistPosition} in line.
              </p>
            </div>
          ) : (
            <form
              id="careentry-waitlist"
              onSubmit={handleWaitlistSubmit}
              data-readdy-form
              className="max-w-2xl mx-auto mb-8 bg-white rounded-2xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-medium text-gray-800 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Join the CareEntry Waitlist
              </h3>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-800 text-lg font-light focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your email address"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-800 text-lg font-light focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                
                <div>
                  <select
                    name="care_providers_count"
                    required
                    className="w-full px-4 py-3 pr-8 rounded-lg border border-gray-300 text-gray-800 text-lg font-light focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    <option value="">How many care providers do you currently manage?</option>
                    <option value="1-3">1-3 providers</option>
                    <option value="4-6">4-6 providers</option>
                    <option value="7-10">7-10 providers</option>
                    <option value="11+">11+ providers</option>
                  </select>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Which care providers do you work with? (Select all that apply)
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid gap-3">
                      {providers.map((provider) => (
                        <div key={provider}>
                          <div className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={provider}
                                checked={selectedProviders[provider]?.selected || false}
                                onChange={(e) => handleProviderChange(provider, 'selected', e.target.checked)}
                                className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                              />
                              <label htmlFor={provider} className="ml-3 text-gray-700 font-medium cursor-pointer">
                                {provider}
                              </label>
                            </div>
                            {selectedProviders[provider]?.selected && (
                              <div className="flex gap-2">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`${provider}_type`}
                                    value={provider === 'IHSS provider' ? 'Family Provider' : 'Agency'}
                                    checked={selectedProviders[provider]?.type === (provider === 'IHSS provider' ? 'Family Provider' : 'Agency')}
                                    onChange={(e) => handleProviderChange(provider, 'type', e.target.value)}
                                    className="w-3 h-3 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 focus:ring-2"
                                  />
                                  <span className="ml-1 text-sm text-gray-600">
                                    {provider === 'IHSS provider' ? 'Family Provider' : 'Agency'}
                                  </span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`${provider}_type`}
                                    value={provider === 'IHSS provider' ? 'Non-Family Provider' : 'Non-Agency'}
                                    checked={selectedProviders[provider]?.type === (provider === 'IHSS provider' ? 'Non-Family Provider' : 'Non-Agency')}
                                    onChange={(e) => handleProviderChange(provider, 'type', e.target.value)}
                                    className="w-3 h-3 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 focus:ring-2"
                                  />
                                  <span className="ml-1 text-sm text-gray-600">
                                    {provider === 'IHSS provider' ? 'Non-Family Provider' : 'Non-Agency'}
                                  </span>
                                </label>
                              </div>
                            )}
                          </div>
                          {provider === 'Others' && selectedProviders[provider]?.selected && (
                            <div className="mt-2 ml-7">
                              <input
                                type="text"
                                placeholder="Please specify other care providers..."
                                value={otherProviderText}
                                onChange={(e) => setOtherProviderText(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div>
                  <textarea
                    name="biggest_challenge"
                    placeholder="What's your biggest challenge coordinating care? (optional)"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-800 text-lg font-light focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer mt-6"
              >
                Join the Waitlist
              </button>
            </form>
          )}

          <p className="text-sm opacity-75 font-light">We'll notify you as soon as CareEntry is ready</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-purple-50 text-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="https://static.readdy.ai/image/d58764125c7980aecc839df102219a66/870d5e2d6ad7495c633feeec16bc35ed.png"
                alt="Care Entry"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-600 font-light">Bringing families and care teams together.</p>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Product
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <a href="#features" className="hover:text-gray-800 cursor-pointer">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-gray-800 cursor-pointer">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="hover:text-gray-800 cursor-pointer">
                    How It Works
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Support
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <a href="#faq" className="hover:text-gray-800 cursor-pointer">
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-gray-800 cursor-pointer">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#help" className="hover:text-gray-800 cursor-pointer">
                    Help Center
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Quick Links
              </h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <a href="#privacy" className="hover:text-gray-800 cursor-pointer">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#terms" className="hover:text-gray-800 cursor-pointer">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="https://readdy.ai/?origin=logo" className="hover:text-gray-800 cursor-pointer">
                    Made with Readdy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-200 mt-8 pt-8 text-center text-purple-600">
            <p className="font-light">&copy; 2024 Care Entry. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

